import { z } from 'zod';
import { documentBlocksSchema, type DocumentBlock } from '../document-template-blocks.js';
import { optionalText, paginationQuerySchema } from './common.js';

/**
 * PDF templates for quotes and invoices.
 *
 * Stored as data rather than written as code, because a template is a thing an
 * administrator changes on a Tuesday afternoon — a logo moved, a payment-terms
 * block added, a second layout for one demanding customer. As code each of
 * those is a deploy, and each tenant's variation is a fork of the layout. As a
 * row it is edited in the app, versioned by the audit log, and rendered
 * through exactly the same pipeline as the built-in layout.
 *
 * The body is HTML with `{{merge.tokens}}` and one repeating
 * `{{#each lines}} … {{/each}}` section. Deliberately not a general template
 * language: repeating its line items is the only thing a document needs beyond
 * substitution, and the moment somebody wants a conditional what they actually
 * want is a second template.
 *
 * An organisation with no templates keeps the built-in layout. Nobody has to
 * author one to send an invoice, and improvements to the built-in one keep
 * arriving.
 */

export const DOCUMENT_TEMPLATE_SCOPES = ['QUOTE', 'INVOICE', 'BOTH', 'CREDIT_NOTE'] as const;
export type DocumentTemplateScope = (typeof DOCUMENT_TEMPLATE_SCOPES)[number];

/**
 * How a template is authored.
 *
 * Both compile to the same `body`/`styles` the PDF pipeline reads — `VISUAL`
 * by way of `compileBlocksToHtml`, `CODE` because the HTML *is* what was
 * typed. Nothing downstream of a save needs to know which one produced it.
 */
export const DOCUMENT_TEMPLATE_MODES = ['VISUAL', 'CODE'] as const;
export type DocumentTemplateMode = (typeof DOCUMENT_TEMPLATE_MODES)[number];

/**
 * The fields shared by create and update, deliberately with no `.default()`
 * on any of them.
 *
 * A default on a field surfaces through `.partial()` too — Zod applies it to
 * an *absent* key regardless, `.partial()` only relaxes what TypeScript (and
 * `.parse()`'s required-key check) demands — so a schema built by making the
 * create schema `.partial()` would resurrect every default-valued field on
 * every update that omitted it: renaming a template would silently reset
 * `appliesTo` to `BOTH` and `isDefault`/`isActive` to `false`/`true`. Instead
 * the shape carries no defaults at all, and `createDocumentTemplateSchema`
 * adds them with `.extend()`, which is the one variant that should ever see
 * an absent key as "use the default" rather than "leave this alone".
 */
const documentTemplateShape = z.object({
  name: z.string().trim().min(1, 'Required').max(120),
  description: optionalText(500),
  appliesTo: z.enum(DOCUMENT_TEMPLATE_SCOPES),
  mode: z.enum(DOCUMENT_TEMPLATE_MODES),
  /**
   * The rendered layout. Required for a `CODE` template (it is what was
   * typed); for a `VISUAL` one the service derives it from `blocks` on
   * every save, so whatever is sent here is ignored and may be omitted.
   */
  body: z.string().max(100_000).optional(),
  /** Extra CSS, so a template restyles the base rather than restating it. */
  styles: optionalText(40_000),
  /** The visual editor's content. Required for `mode: 'VISUAL'`, absent otherwise. */
  blocks: documentBlocksSchema.optional(),
  /**
   * The one used when a document does not name a template.
   *
   * At most one per scope; setting a new one clears the old, in the service,
   * inside the same transaction.
   */
  isDefault: z.coerce.boolean(),
  isActive: z.coerce.boolean(),
});

export const createDocumentTemplateSchema = documentTemplateShape
  .extend({
    appliesTo: z.enum(DOCUMENT_TEMPLATE_SCOPES).default('BOTH'),
    mode: z.enum(DOCUMENT_TEMPLATE_MODES).default('CODE'),
    isDefault: z.coerce.boolean().default(false),
    isActive: z.coerce.boolean().default(true),
  })
  .refine((value) => value.mode !== 'CODE' || Boolean(value.body?.trim()), {
    message: 'A code template needs a body',
    path: ['body'],
  })
  .refine((value) => value.mode !== 'VISUAL' || (value.blocks?.length ?? 0) > 0, {
    message: 'Add at least one block',
    path: ['blocks'],
  });
export type CreateDocumentTemplateInput = z.infer<typeof createDocumentTemplateSchema>;

export const updateDocumentTemplateSchema = documentTemplateShape.partial();
export type UpdateDocumentTemplateInput = z.infer<typeof updateDocumentTemplateSchema>;

export const listDocumentTemplatesQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(['name', 'createdAt', 'updatedAt']).default('name'),
  appliesTo: z.enum(DOCUMENT_TEMPLATE_SCOPES).optional(),
});
export type ListDocumentTemplatesQuery = z.infer<typeof listDocumentTemplatesQuerySchema>;

/** A document kind the template system renders — whatever `appliesTo` scopes exist for. */
export const PREVIEWABLE_DOCUMENT_KINDS = ['quote', 'invoice', 'credit-note'] as const;
export type PreviewableDocumentKind = (typeof PREVIEWABLE_DOCUMENT_KINDS)[number];

/**
 * Previewing a template exactly as it is being edited — including a change
 * nobody has saved yet — against safe, made-up sample data rather than a
 * real customer's document.
 *
 * The same shape `documentTemplateShape` validates `mode`/`body`/`blocks`
 * with, because this renders through the identical compile step
 * (`compileBlocksToHtml` for `VISUAL`, the typed HTML for `CODE`) the real
 * save path uses — a preview that used a different renderer could show a
 * template rendering correctly and then fail, or vice versa, once saved.
 */
export const previewDocumentTemplateSchema = z
  .object({
    kind: z.enum(PREVIEWABLE_DOCUMENT_KINDS),
    mode: z.enum(DOCUMENT_TEMPLATE_MODES),
    body: z.string().max(100_000).optional(),
    styles: optionalText(40_000),
    blocks: documentBlocksSchema.optional(),
  })
  .refine((value) => value.mode !== 'CODE' || Boolean(value.body?.trim()), {
    message: 'A code template needs a body',
    path: ['body'],
  })
  .refine((value) => value.mode !== 'VISUAL' || (value.blocks?.length ?? 0) > 0, {
    message: 'Add at least one block',
    path: ['blocks'],
  });
export type PreviewDocumentTemplateInput = z.infer<typeof previewDocumentTemplateSchema>;

export interface DocumentTemplateSummary {
  id: string;
  name: string;
  description: string | null;
  appliesTo: DocumentTemplateScope;
  mode: DocumentTemplateMode;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentTemplateDetail extends DocumentTemplateSummary {
  body: string;
  styles: string | null;
  /** Null unless `mode` is `VISUAL`. */
  blocks: DocumentBlock[] | null;
  /** Tokens this body references that the renderer does not supply. */
  unknownTokens: string[];
}

/**
 * Every token a document template may use.
 *
 * Served to the editor so somebody writing a template can see what is
 * available rather than guessing — and so an unknown token is reported as a
 * mistake rather than silently rendering as nothing.
 */
export interface MergeVariable {
  token: string;
  description: string;
}

export const DOCUMENT_MERGE_VARIABLES: readonly MergeVariable[] = [
  { token: 'document.kind', description: 'Quote or Invoice' },
  { token: 'document.number', description: 'The document number, e.g. INV-00007' },
  { token: 'document.subject', description: 'What the document is for' },
  { token: 'document.status', description: 'Its current status' },
  { token: 'document.date', description: 'The document date' },
  { token: 'document.dueDate', description: 'Due or valid-until date, whichever applies' },
  { token: 'document.currency', description: 'The currency code it is raised in' },
  { token: 'document.subtotal', description: 'Before discount and tax' },
  { token: 'document.discount', description: 'Total discount' },
  { token: 'document.tax', description: 'Total tax' },
  { token: 'document.adjustment', description: 'Adjustment, on orders and invoices' },
  { token: 'document.total', description: 'What the customer owes' },
  { token: 'document.terms', description: 'Terms and conditions' },
  { token: 'document.salesPerson', description: 'The business manager, as the sales person' },
  { token: 'document.paymentTerms', description: 'Invoices: Due on Receipt, Net 15, …' },
  { token: 'document.amountPaid', description: 'Invoices: what has been paid' },
  { token: 'document.balanceDue', description: 'Invoices: what is still owed' },
  {
    token: 'document.totalInWords',
    description: 'The total written out, in the document’s currency',
  },
  {
    token: 'document.metaHtml',
    description: 'Number, dates, terms and sales person as ready-made rows',
  },
  {
    token: 'document.summaryHtml',
    description: 'The totals (and payment, on an invoice) as ready-made rows',
  },
  { token: 'document.baseTotal', description: 'The total in the organisation’s own currency' },
  { token: 'document.baseCurrency', description: 'The organisation’s own currency code' },
  { token: 'document.exchangeRate', description: 'The rate frozen on the document' },
  { token: 'document.exchangeRateAt', description: 'The date that rate was published' },

  { token: 'billTo.name', description: 'Who it is addressed to' },
  { token: 'billTo.contactName', description: 'The named contact' },
  { token: 'billTo.email', description: 'Their email address' },
  { token: 'billTo.address', description: 'Their address, already line-broken' },
  { token: 'billTo.phone', description: 'Their phone number' },
  { token: 'billTo.gstNumber', description: 'Their GST number' },
  {
    token: 'billTo.detailsHtml',
    description: 'Name, contact, address, email, phone and GSTIN — only what exists',
  },

  { token: 'org.name', description: 'Your organisation’s name' },
  { token: 'org.address', description: 'Your address, already line-broken' },
  { token: 'org.email', description: 'Your billing email' },
  { token: 'org.phone', description: 'Your phone number' },
  { token: 'org.website', description: 'Your website' },
  { token: 'org.taxId', description: 'Your tax registration number' },
  { token: 'org.taxIdLabel', description: 'What you call it — VAT, GSTIN' },
  { token: 'org.logoUrl', description: 'Your logo’s URL, for an <img src>' },
  { token: 'org.accentColor', description: 'Your accent colour, for CSS' },
  { token: 'org.logoHtml', description: 'Your logo as an <img>, or nothing if you have none' },
  { token: 'org.contactLine', description: 'Phone, email and website on one line' },
  { token: 'org.contactLines', description: 'Phone, email and website, one per line' },
  { token: 'org.taxLine', description: 'Your tax registration, labelled — GSTIN: …' },
  { token: 'org.footerNote', description: 'The note at the foot of every document' },

  { token: 'lines', description: 'Repeat with {{#each lines}} … {{/each}}' },
  { token: 'description', description: 'Inside a line: what was sold' },
  { token: 'quantity', description: 'Inside a line: how many' },
  { token: 'unitPrice', description: 'Inside a line: price each' },
  { token: 'discountPercent', description: 'Inside a line: its discount' },
  { token: 'taxRate', description: 'Inside a line: its tax rate' },
  { token: 'lineTotal', description: 'Inside a line: the line’s total' },
];

/** The tokens a body uses that the renderer does not supply. */
export function unknownMergeTokens(tokens: readonly string[]): string[] {
  const known = new Set(DOCUMENT_MERGE_VARIABLES.map((variable) => variable.token));
  return tokens.filter((token) => !known.has(token));
}
